import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Divider,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { ReactComponent as GenerateButton } from '../../../images/generateButton.svg';
import { useNavigate, useLocation } from 'react-router-dom';
import { bitbucketNewProjectCreation } from '../../../services/webServices';
import { LoadingModalContext } from '../../main-pages/ServicePage';
import BrowsePathPopup from './BrowsePathPopup';

function NewProjectBitbucketCredentialsPage() {
  const [workspace, setWorkspace] = useState('');
  const [repoName, setRepoName] = useState('');
  const [projectKey, setProjectKey] = useState('');
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [projectType, setProjectType] = useState('');
  const [framework, setFramework] = useState('');
  const [zipFile, setZipFile] = useState(null);
  const [popUpOpen, setPopUpOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [bitbucketpr, setBitbucketpr] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { setLoadingState } = useContext(LoadingModalContext);

  useEffect(() => {
    setProjectType(location.state?.projectType || '');
    setFramework(location.state?.framework || '');
    setZipFile(location.state?.zipFile);
  }, [location.state?.projectType, location.state?.framework, location.state?.zipFile]);

  const validateForm = () =>
    workspace && repoName && projectKey && consumerKey && consumerSecret;

  const handleConfirmClick = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);

    let fake;
    const startProgress = (setProgress) => {
      let progress = 0;
      let stop = false;
      let finalMessageShown = false;

      const interval = setInterval(() => {
        if (stop) return;
        progress += 0.001;
        if (progress >= 97 && !finalMessageShown) {
          finalMessageShown = true;
          setLoadingState((prev) => ({
            ...prev,
            message: 'Almost done! Fetching your output',
          }));
        }
        if (progress >= 97) progress = 97;
        setProgress((prev) => ({
          ...prev,
          progress: Math.max(prev.progress, Math.floor(progress)),
        }));
      }, 200);

      return {
        stop: () => {
          stop = true;
          clearInterval(interval);
        },
        completeTo100: async () => {
          stop = true;
          clearInterval(interval);
          setProgress((prev) => ({
            ...prev,
            progress: 100,
          }));
          await new Promise((res) => setTimeout(res, 500));
        },
      };
    };

    const finishTo100AndClose = async () => {
      await fake.completeTo100();
      setLoadingState({ open: false, message: '', progress: null });
    };

    setLoadingState({
      open: true,
      message: 'Uploading a UI Test Automation Project to Bitbucket',
      progress: 0,
    });

    fake = startProgress(setLoadingState);

    try {
      const formData = new FormData();
      formData.append('workspace', workspace);
      formData.append('repoName', repoName);
      formData.append('projectKey', projectKey);
      formData.append('consumerKey', consumerKey);
      formData.append('consumerSecret', consumerSecret);
      formData.append('zipfile', zipFile, 'project.zip');

      console.log('Sending FormData with:', {
        workspace,
        repoName,
        projectKey,
        consumerKey,
        consumerSecret: consumerSecret ? 'provided' : 'missing',
        zipFile: zipFile ? 'present' : 'missing',
      });

      const bitbucketNewProject = await bitbucketNewProjectCreation(formData);

      if (bitbucketNewProject && bitbucketNewProject.data) {
        const enhancedPrDetails = {
          ...bitbucketNewProject.data,
          repository:
            bitbucketNewProject.data.repository ||
            `${workspace}/${repoName}`,
          pullRequestUrl:
            bitbucketNewProject.data.pullRequestUrl ||
            `https://bitbucket.org/${workspace}/${repoName}/pull-requests/${bitbucketNewProject.data.pullRequestNumber}`,
        };

        setBitbucketpr(enhancedPrDetails);
        setStatusMessage(`✅ Your Bitbucket repository was created successfully!`);
        await finishTo100AndClose();
      }
      setPopUpOpen(true);
    } catch (error) {
      console.error('Error uploading project to Bitbucket:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'An error occurred while uploading the project to Bitbucket.';

      setStatusMessage(errorMessage);
      setPopUpOpen(true);
      setLoadingState({ open: false, message: '', progress: null });
    } finally {
      setLoadingState({ open: false, message: '', progress: null });
      setIsSubmitting(false);
    }
  };

  const handleClose = () => setPopUpOpen(false);

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#f7f4f2"
    >
      <Paper elevation={1} sx={{ p: 3, width: 520 }}>
        <Box mb={2}>
          <Typography variant="subtitle1" fontWeight={500}>
            <ExpandMore sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
            Bitbucket Configuration (OAuth 1.0a)
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 2, fontSize: '0.875rem' }}>
          Provide your <strong>Consumer Key</strong> and <strong>Consumer Secret</strong> from the OAuth consumer in Bitbucket settings.
        </Alert>

        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            fullWidth
            label="Workspace Slug"
            placeholder="workspace-name"
            variant="outlined"
            value={workspace}
            onChange={(e) => setWorkspace(e.target.value.trim())}
            helperText="Example: qualizeal (slug, not display name)"
            disabled={isSubmitting}
            required
          />

          <TextField
            fullWidth
            label="Repository Name"
            placeholder="repo-name"
            variant="outlined"
            value={repoName}
            onChange={(e) => setRepoName(e.target.value)}
            helperText="Lowercase letters, numbers, hyphens, and underscores only"
            disabled={isSubmitting}
            required
          />

          <TextField
            fullWidth
            label="Project Key"
            placeholder="PROJ"
            variant="outlined"
            value={projectKey}
            onChange={(e) => setProjectKey(e.target.value.toUpperCase())}
            inputProps={{ style: { textTransform: 'uppercase' } }}
            helperText="Must be an existing project key in uppercase"
            disabled={isSubmitting}
            required
          />

          <Divider sx={{ my: 1.5 }} />

          <TextField
            fullWidth
            label="Consumer Key"
            placeholder="Enter OAuth consumer key"
            variant="outlined"
            value={consumerKey}
            onChange={(e) => setConsumerKey(e.target.value)}
            disabled={isSubmitting}
            required
          />

          <TextField
            fullWidth
            label="Consumer Secret"
            placeholder="Enter OAuth consumer secret"
            variant="outlined"
            type="password"
            value={consumerSecret}
            onChange={(e) => setConsumerSecret(e.target.value)}
            disabled={isSubmitting}
            required
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box mt={2} textAlign="right">
          <Button
            startIcon={<GenerateButton />}
            variant="contained"
            sx={{ textTransform: 'none' }}
            onClick={handleConfirmClick}
            disabled={!validateForm() || isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Confirm'}
          </Button>
        </Box>
      </Paper>

      <BrowsePathPopup
        open={popUpOpen}
        handleClose={handleClose}
        text1={statusMessage}
        prDetails={bitbucketpr}
        provider="bitbucket"
      />
    </Box>
  );
}

export default NewProjectBitbucketCredentialsPage;
